import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertDialog,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  Flex,
  Heading,
  IconButton,
  Select,
  Text,
  TextArea
} from '@radix-ui/themes'
import { Pencil1Icon, TrashIcon } from '@radix-ui/react-icons'
import { useMemo, useState } from 'react'
import cn from 'classnames'
import { Comments, Ops } from '@willie/schemas'
import { useTRPC, type TRPCOutputs } from '~/utils/trpc'
import { formatTimestamp } from '~/utils/date'
import {
  INSTALLATION_EVENT_TONE,
  STALLED_BECAUSE_OF_OPTIONS,
  installationEventLabel,
  stalledBecauseOfLabel
} from '~/features/installations/constants'

type StalledBecauseOf = Ops.StationInstallations.StalledBecauseOf
type InstallationEvent = Ops.StationInstallations.InstallationEvent
type CommentWithAuthor = TRPCOutputs['ops']['stationInstallations']['comments']['list'][number]

type TimelineItem =
  | { kind: 'comment'; occurredAt: string; comment: CommentWithAuthor }
  | { kind: 'event'; occurredAt: string; event: InstallationEvent }

export const eventDomId = (eventId: number): string => `event-${eventId}`

const commentDomId = (commentId: string): string => `comment-${commentId}`
const { COMMENT_MAX_LENGTH } = Comments

const initialFromEmail = (email: string | null): string => {
  if (!email) return '?'
  return email.charAt(0).toUpperCase()
}

interface CommentEditorProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  onCancel?: () => void
  isPending: boolean
  errorMessage?: string
  submitLabel: string
  size?: '1' | '2'
  rows?: number
  placeholder?: string
}

const CommentEditor = ({
  value,
  onChange,
  onSubmit,
  onCancel,
  isPending,
  errorMessage,
  submitLabel,
  size = '2',
  rows = 3,
  placeholder
}: CommentEditorProps) => {
  const trimmed = value.trim()
  const canSubmit = trimmed.length > 0 && !isPending

  return (
    <Flex direction="column" gap="2">
      <TextArea
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        maxLength={COMMENT_MAX_LENGTH}
        rows={rows}
        disabled={isPending}
      />
      {errorMessage ? (
        <Text color="red" size="1">
          {errorMessage}
        </Text>
      ) : null}
      <Flex gap="2" justify="end">
        {onCancel ? (
          <Button variant="soft" color="gray" size={size} onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
        ) : null}
        <Button size={size} onClick={onSubmit} disabled={!canSubmit} loading={isPending}>
          {submitLabel}
        </Button>
      </Flex>
    </Flex>
  )
}

interface CommentItemProps {
  comment: CommentWithAuthor
  installationId: number
}

const CommentItem = ({ comment, installationId }: CommentItemProps) => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(comment.body)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.ops.stationInstallations.comments.list.queryKey({ installationId })
    })

  const updateMutation = useMutation(
    trpc.ops.stationInstallations.comments.update.mutationOptions({
      onSuccess: () => {
        invalidate()
        setIsEditing(false)
      }
    })
  )

  const deleteMutation = useMutation(
    trpc.ops.stationInstallations.comments.delete.mutationOptions({
      onSuccess: () => {
        invalidate()
        setDeleteOpen(false)
      }
    })
  )

  const authorEmail = comment.author?.email ?? null
  const trimmedDraft = draft.trim()
  const isUnchanged = trimmedDraft === comment.body

  const handleStartEdit = () => {
    setDraft(comment.body)
    setIsEditing(true)
    updateMutation.reset()
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setDraft(comment.body)
    updateMutation.reset()
  }

  const handleSaveEdit = () => {
    if (isUnchanged) return
    updateMutation.mutate({ commentId: comment.id, body: trimmedDraft })
  }

  return (
    <Flex id={commentDomId(comment.id)} gap="3" align="start">
      <Avatar size="2" radius="full" fallback={initialFromEmail(authorEmail)} color="gray" variant="soft" />
      <Flex direction="column" gap="1" className="min-w-0 flex-1">
        <Flex align="center" gap="2" wrap="wrap">
          <Text size="2" weight="medium">
            {authorEmail ?? 'Unknown'}
          </Text>
          <Text size="1" color="gray">
            {formatTimestamp(comment.created_at)}
          </Text>
          {comment.edited_at ? (
            <Text size="1" color="gray">
              (edited)
            </Text>
          ) : null}
          {!isEditing ? (
            <Flex gap="1" ml="auto">
              <IconButton
                size="1"
                variant="ghost"
                color="gray"
                onClick={handleStartEdit}
                aria-label="Edit comment"
              >
                <Pencil1Icon />
              </IconButton>
              <AlertDialog.Root open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialog.Trigger>
                  <IconButton size="1" variant="ghost" color="gray" aria-label="Delete comment">
                    <TrashIcon />
                  </IconButton>
                </AlertDialog.Trigger>
                <AlertDialog.Content>
                  <AlertDialog.Title>Delete comment?</AlertDialog.Title>
                  <AlertDialog.Description size="2">This cannot be undone.</AlertDialog.Description>
                  {deleteMutation.error ? (
                    <Text color="red" size="2" as="p" mt="3">
                      {deleteMutation.error.message}
                    </Text>
                  ) : null}
                  <Flex gap="3" mt="4" justify="end">
                    <AlertDialog.Cancel>
                      <Button variant="soft" color="gray" disabled={deleteMutation.isPending}>
                        Cancel
                      </Button>
                    </AlertDialog.Cancel>
                    <Button
                      color="red"
                      loading={deleteMutation.isPending}
                      disabled={deleteMutation.isPending}
                      onClick={() => deleteMutation.mutate({ commentId: comment.id })}
                    >
                      Delete
                    </Button>
                  </Flex>
                </AlertDialog.Content>
              </AlertDialog.Root>
            </Flex>
          ) : null}
        </Flex>

        {isEditing ? (
          <CommentEditor
            value={draft}
            onChange={setDraft}
            onSubmit={handleSaveEdit}
            onCancel={handleCancelEdit}
            isPending={updateMutation.isPending}
            errorMessage={updateMutation.error?.message}
            submitLabel="Save"
            size="1"
          />
        ) : (
          <Text size="2" className="whitespace-pre-wrap break-words">
            {comment.body}
          </Text>
        )}
      </Flex>
    </Flex>
  )
}

const InstallationEventItem = ({ event }: { event: InstallationEvent }) => {
  const assigneeName = Ops.StationInstallations.assigneeDisplayName(event)
  const isOpenIssue = INSTALLATION_EVENT_TONE[event.type] === 'red' && !event.resolvedAt

  return (
    <Flex
      id={eventDomId(event.id)}
      gap="3"
      align="start"
      className={cn('border-l-2 pl-3', isOpenIssue ? 'border-l-[color:var(--red-8)]' : 'border-l-[color:var(--gray-6)]')}
    >
      <Avatar
        size="2"
        radius="full"
        fallback={initialFromEmail(event.createdByEmail)}
        color={isOpenIssue ? 'red' : 'gray'}
        variant="soft"
      />
      <Flex direction="column" gap="1" className="min-w-0 flex-1">
        <Flex align="center" gap="2" wrap="wrap">
          <Text size="2" weight="medium">
            {event.createdByEmail ?? 'System'}
          </Text>
          <Text size="1" color="gray">
            {formatTimestamp(event.occurredAt)}
          </Text>
          <Badge color={isOpenIssue ? 'red' : 'gray'} variant="soft" size="1">
            {installationEventLabel[event.type]}
            {/* Only stalls carry a reason worth naming; assistant events are all category 'client'. */}
            {event.type === 'stalled' && event.category ? ` · ${stalledBecauseOfLabel[event.category]}` : ''}
            {event.type === 'stalled' && event.resolvedAt ? ' · resumed' : ''}
          </Badge>
          {assigneeName ? (
            <Badge color="gray" variant="soft" size="1">
              Assigned: {assigneeName}
            </Badge>
          ) : null}
        </Flex>
        <Text size="2" className="whitespace-pre-wrap break-words">
          {event.note}
        </Text>
      </Flex>
    </Flex>
  )
}

const toTimelineItems = (comments: CommentWithAuthor[], events: InstallationEvent[]): TimelineItem[] =>
  [
    ...comments.map(
      (comment): TimelineItem => ({
        kind: 'comment',
        occurredAt: comment.created_at,
        comment
      })
    ),
    ...events.map(
      (event): TimelineItem => ({
        kind: 'event',
        occurredAt: event.occurredAt,
        event
      })
    )
  ].sort((left, right) => left.occurredAt.localeCompare(right.occurredAt))

interface TimelineListProps {
  installationId: number
  items: TimelineItem[]
  isLoading: boolean
  errorMessage?: string
}

const TimelineList = ({ installationId, items, isLoading, errorMessage }: TimelineListProps) => {
  if (isLoading) {
    return (
      <Text size="2" color="gray">
        Loading timeline…
      </Text>
    )
  }
  if (errorMessage) {
    return (
      <Text size="2" color="red">
        {errorMessage}
      </Text>
    )
  }
  if (items.length === 0) {
    return (
      <Text size="2" color="gray">
        No activity yet.
      </Text>
    )
  }
  return (
    <Flex direction="column" gap="4">
      {items.map((item) =>
        item.kind === 'comment' ? (
          <CommentItem key={`comment-${item.comment.id}`} comment={item.comment} installationId={installationId} />
        ) : (
          <InstallationEventItem key={`event-${item.event.id}`} event={item.event} />
        )
      )}
    </Flex>
  )
}

interface InstallationCommentsProps {
  installationId: number
  isAlreadyStalled: boolean
  events: InstallationEvent[]
}

export const InstallationComments = ({ installationId, isAlreadyStalled, events }: InstallationCommentsProps) => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState('')
  const [stallDialogOpen, setStallDialogOpen] = useState(false)
  const [stalledBecauseOf, setStalledBecauseOf] = useState<StalledBecauseOf>('client')

  const commentsQuery = useQuery(trpc.ops.stationInstallations.comments.list.queryOptions({ installationId }))

  const invalidateComments = () =>
    queryClient.invalidateQueries({
      queryKey: trpc.ops.stationInstallations.comments.list.queryKey({ installationId })
    })

  const invalidateEvents = () => {
    queryClient.invalidateQueries({
      queryKey: trpc.ops.stationInstallations.events.list.queryKey({ installationId })
    })
    queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.list.queryKey() })
    queryClient.invalidateQueries({ queryKey: trpc.ops.stationInstallations.listStalled.queryKey() })
  }

  const createCommentMutation = useMutation(
    trpc.ops.stationInstallations.comments.create.mutationOptions({
      onSuccess: () => {
        invalidateComments()
        setDraft('')
      }
    })
  )

  const createEventMutation = useMutation(
    trpc.ops.stationInstallations.events.create.mutationOptions({
      onSuccess: () => {
        invalidateEvents()
        setDraft('')
        setStallDialogOpen(false)
      }
    })
  )

  const handleSubmit = () => {
    const trimmed = draft.trim()
    if (trimmed.length === 0 || createCommentMutation.isPending) return
    createCommentMutation.mutate({ installationId, body: trimmed })
  }

  const handleOpenStallDialog = () => {
    setStalledBecauseOf('client')
    createEventMutation.reset()
    setStallDialogOpen(true)
  }

  const handleConfirmStall = () => {
    const trimmed = draft.trim()
    if (trimmed.length === 0 || createEventMutation.isPending) return
    createEventMutation.mutate({
      installationId,
      type: 'stalled',
      note: trimmed,
      category: stalledBecauseOf
    })
  }

  const comments = commentsQuery.data ?? []
  const timelineItems = useMemo(() => toTimelineItems(comments, events), [comments, events])
  const trimmedDraft = draft.trim()
  const canStall = trimmedDraft.length > 0 && !isAlreadyStalled

  return (
    <Card>
      <Heading size="3" mb="3">
        Timeline
      </Heading>

      <Flex direction="column" mb="4">
        <TimelineList
          installationId={installationId}
          items={timelineItems}
          isLoading={commentsQuery.isLoading}
          errorMessage={commentsQuery.error?.message}
        />
      </Flex>

      <div className={cn(timelineItems.length > 0 && 'border-t border-t-[color:var(--gray-5)] pt-4')}>
        <Flex direction="column" gap="2">
          <TextArea
            placeholder="Add a comment…"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            maxLength={COMMENT_MAX_LENGTH}
            rows={3}
            disabled={createCommentMutation.isPending || createEventMutation.isPending}
          />
          {createCommentMutation.error ? (
            <Text color="red" size="1">
              {createCommentMutation.error.message}
            </Text>
          ) : null}
          <Flex gap="2" justify="end">
            <Button
              variant="soft"
              color="red"
              disabled={!canStall || createEventMutation.isPending}
              onClick={handleOpenStallDialog}
              title={isAlreadyStalled ? 'Already stalled' : undefined}
            >
              Add note and mark as stalled
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={trimmedDraft.length === 0 || createCommentMutation.isPending}
              loading={createCommentMutation.isPending}
            >
              Add comment
            </Button>
          </Flex>
        </Flex>
      </div>

      <Dialog.Root open={stallDialogOpen} onOpenChange={setStallDialogOpen}>
        <Dialog.Content maxWidth="400px">
          <Dialog.Title>Mark installation as stalled</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            Your note will be added to the timeline and the installation will be marked as stalled.
          </Dialog.Description>
          <Box mt="3">
            <Text as="label" size="1" color="gray" className="mb-1 block">
              Stalled because of
            </Text>
            <Select.Root
              value={stalledBecauseOf}
              onValueChange={(value) => setStalledBecauseOf(value as StalledBecauseOf)}
            >
              <Select.Trigger className="w-full" />
              <Select.Content>
                {STALLED_BECAUSE_OF_OPTIONS.map((option) => (
                  <Select.Item key={option} value={option}>
                    {stalledBecauseOfLabel[option]}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Box>
          {createEventMutation.error ? (
            <Text color="red" size="2" as="p" mt="3">
              {createEventMutation.error.message}
            </Text>
          ) : null}
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray" disabled={createEventMutation.isPending}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              color="red"
              onClick={handleConfirmStall}
              loading={createEventMutation.isPending}
              disabled={createEventMutation.isPending}
            >
              Confirm
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Card>
  )
}
